import { Button, TextField, Box, Select, MenuItem, Card, CardContent, InputAdornment, IconButton, Typography, Avatar, Divider, FormControl, InputLabel } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LogTheme from '../Layout/LogTheme';
import { useAuth } from '../../contexts/AuthContext';
import ImageUploader from '../Layout/ImageUploader';
import axios from 'axios';
import { validatePassword, getPasswordStrength } from '../../utils/passwordValidation';
import { getAuthHeaders, getAuthHeader, getImageDisplayUrl, getUploadApiBaseUrl } from '../../utils/api';

function ChangeUser() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [bio, setBio] = useState('');
  const [profilePictures, setProfilePictures] = useState([]);
  const [role, setRole] = useState('user');
  const { userId } = useParams();
  const { userRole } = useAuth();
  const navigate = useNavigate();
  const [imagesToRemove, setImagesToRemove] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [passwordError, setPasswordError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // Kontrollera att användaren är superadmin
    if (userRole !== 'superadmin') {
      navigate('/admin'); // Omdirigera till adminpanelen
      return;
    }
  }, [userRole, navigate]);

  useEffect(() => {
    if (userId && userRole === 'superadmin') {
      fetch(`${process.env.REACT_APP_SERVER_URL}/api/showprofile/${userId}`, {
        headers: getAuthHeaders()
      })
        .then(response => {
          if (response.status === 401) {
            navigate('/info');
            return Promise.reject('Unauthorized');
          }
          return response.json();
        })
        .then(data => {
          setUsername(data.username || '');
          setEmail(data.email || '');
          setFirstName(data.firstName || '');
          setLastName(data.lastName || '');
          setBio(data.bio || '');
          setRole(data.role || 'user');
          
          // Hantera profilePictures - kan vara array, null eller undefined
          let profilePicturesArray = data.profilePictures || [];
          if (typeof profilePicturesArray === 'string') {
            try {
              profilePicturesArray = JSON.parse(profilePicturesArray);
            } catch (e) {
              profilePicturesArray = [];
            }
          }
          if (!Array.isArray(profilePicturesArray)) {
            profilePicturesArray = [];
          }
          setProfilePictures(profilePicturesArray);
          
          // Rensa lösenordsfälten
          setPassword('');
          setConfirmPassword('');
          setPasswordError('');
          setPasswordStrength('');
          setError(null);
        })
        .catch(error => {
          console.error('Error:', error);
          setError('Kunde inte hämta användaruppgifter.');
        });
    }
  }, [userId, userRole, navigate]);

  const handleRemoveExistingImage = (event, indexToRemove) => {
    event.preventDefault();
    
    // Använd profilePictures state direkt
    const currentPictures = Array.isArray(profilePictures) ? profilePictures : [];
    if (currentPictures.length === 0) return;
    
    const imageToRemove = currentPictures[0];
    setImagesToRemove(prev => [...prev, imageToRemove]);

    // Ta bort bilden från ProfilePictures-staten (töm arrayen) - detta uppdaterar visningen direkt
    setProfilePictures([]);
  };

  const handleUploadSuccess = (files) => {
    setSelectedFiles(prevFiles => [...prevFiles, ...files]);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    
    // Om lösenordet är tomt, rensa fel
    if (newPassword.length === 0) {
      setPasswordError('');
      setPasswordStrength('');
      setError(null);
      return;
    }
    
    // Validera lösenord om det anges
    const errors = validatePassword(newPassword);
    if (errors.length > 0) {
      setPasswordError(errors.join(', '));
      setPasswordStrength('weak');
    } else {
      setPasswordError('');
      setPasswordStrength(getPasswordStrength(newPassword));
    }
    
    // Kontrollera matchning
    if (confirmPassword && newPassword !== confirmPassword) {
      setError('Lösenorden matchar inte');
    } else {
      setError(null);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    
    if (newConfirmPassword && newConfirmPassword !== password) {
      setError('Lösenorden matchar inte');
    } else {
      setError(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    // Validera lösenord om det anges
    if (password) {
      const passwordErrors = validatePassword(password);
      if (passwordErrors.length > 0) {
        setError(`Lösenordet uppfyller inte kraven: ${passwordErrors.join(', ')}`);
        return;
      }

      // Kontrollera att lösenorden matchar
      if (password !== confirmPassword) {
        setError('Lösenorden matchar inte');
        return;
      }
    }

    try {
      // Hantera profilbilder
      let updatedImages = Array.isArray(profilePictures) ? profilePictures : [];
      
      // Om det finns nya filer att ladda upp
      if (selectedFiles.length > 0) {
        const formData = new FormData();
        selectedFiles.forEach((file, index) => {
          formData.append(`file${index}`, file);
        });

        const uploadResponse = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/uploads/profile-pictures`, formData, {
          headers: getAuthHeader() ? { Authorization: getAuthHeader() } : {},
        });

        if (uploadResponse.status === 200) {
          const urls = (uploadResponse.data || []).map(f => f.url).filter(Boolean);
          if (updatedImages.length > 0 && urls.length > 0) {
            setImagesToRemove(prev => [...prev, updatedImages[0]]);
          }
          updatedImages = urls.length > 0 ? [urls[0]] : updatedImages;
        } else {
          throw new Error('Fel vid filuppladdning.');
        }
      }

      imagesToRemove.forEach(urlOrPath => {
        fetch(`${getUploadApiBaseUrl()}/api/deleteimage`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
          body: JSON.stringify({ url: urlOrPath })
        })
          .then(response => response.text())
          .then(data => {
            console.log(data);
          })
          .catch((error) => {
            console.error('Error:', error);
          });
      });

      // Skapa ett objekt med uppdaterade data att skicka till servern
      // VIKTIGT: Skicka INTE email eftersom det inte ska kunna ändras
      const userData = {
        username,
        firstName,
        lastName,
        bio,
        profilePictures: updatedImages,
        role
      };

      // Om lösenordet inte är tomt, lägg till det i userData
      if (password.trim() !== '') {
        userData.password = password;
      }

      // Skicka till servern
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/updateuser/${userId}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData)
      });

      if (response.ok) {
        navigate('/admin'); // Navigera tillbaka till adminpanelen
      } else {
        const errorData = await response.text();
        setError('Kunde inte uppdatera användaren: ' + errorData);
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Något gick fel vid uppdatering.');
    }
  };

  // Säkerställ att profilePictures är en array
  const profilePicturesArray = Array.isArray(profilePictures) ? profilePictures : [];
  const mainProfilePicture = profilePicturesArray.length > 0 ? profilePicturesArray[0] : null;

  if (userRole !== 'superadmin') {
    return null; // Komponenten kommer att navigera bort
  }

  return (
    <LogTheme>
      <Box sx={{ maxWidth: '800px', margin: '0 auto', padding: { xs: 2, md: 4 } }}>
        <Card sx={{ padding: 4 }}>
          <CardContent>
            <Typography variant="h4" component="h1" gutterBottom>
              Ändra användare
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 4 }}>
              Uppdatera användaruppgifter och profilbild
            </Typography>

            <Divider sx={{ mb: 4 }} />

            <form onSubmit={handleSubmit}>
              {/* Profile Picture Section */}
              <Box mb={4}>
                <Typography variant="h6" gutterBottom>
                  Profilbild
                </Typography>
                <Box display="flex" justifyContent="center" mb={3}>
                  {mainProfilePicture ? (
                    <Avatar
                      src={getImageDisplayUrl(mainProfilePicture)}
                      alt="Profilbild"
                      sx={{ width: 150, height: 150 }}
                    />
                  ) : (
                    <AccountCircleIcon sx={{ fontSize: 150, color: 'action.disabled' }} />
                  )}
                </Box>
                <Box mb={2} display="flex" flexDirection="column" alignItems="center" gap={2}>
                  <ImageUploader onUploadSuccess={handleUploadSuccess} />
                  {mainProfilePicture && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={(event) => {
                        event.preventDefault();
                        if (profilePicturesArray.length > 0) {
                          handleRemoveExistingImage(event, 0);
                        }
                      }}
                    >
                      Ta bort profilbild
                    </Button>
                  )}
                </Box>
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Profile Information Section */}
              <Box mb={4}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Profilinformation
                </Typography>
                <Box mb={3}>
                  <TextField
                    type="email"
                    label="E-post"
                    value={email}
                    fullWidth
                    disabled
                    helperText="E-postadressen kan inte ändras"
                  />
                </Box>
                <Box mb={3}>
                  <TextField
                    type="text"
                    label="Namn"
                    value={`${firstName || ''} ${lastName || ''}`.trim() || ''}
                    onChange={(e) => {
                      const names = e.target.value.split(' ');
                      setFirstName(names[0] || '');
                      setLastName(names.slice(1).join(' ') || '');
                    }}
                    fullWidth
                    helperText="Förnamn och efternamn"
                  />
                </Box>
                <Box mb={3}>
                  <TextField
                    type="text"
                    label="Användarnamn"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    fullWidth
                  />
                </Box>
                <Box mb={3}>
                  <FormControl fullWidth>
                    <InputLabel>Roll</InputLabel>
                    <Select
                      value={role}
                      label="Roll"
                      onChange={(e) => setRole(e.target.value)}
                    >
                      <MenuItem value="user">User</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="superadmin">Superadmin</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box mb={3}>
                  <TextField
                    multiline
                    rows={4}
                    label="Beskrivning"
                    placeholder="Skriv lite om båten och besättningen ..."
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    fullWidth
                  />
                </Box>
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Password Section */}
              <Box mb={4}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Lösenord
                </Typography>
                <Box mb={2}>
                  <TextField
                    type={showPassword ? 'text' : 'password'}
                    label="Lösenord (lämna tomt för att behålla nuvarande)"
                    value={password}
                    onChange={handlePasswordChange}
                    error={!!passwordError}
                    helperText={
                      passwordError 
                        ? passwordError 
                        : passwordStrength === 'strong' 
                          ? 'Starkt lösenord ✓' 
                          : passwordStrength === 'medium' 
                            ? 'Medelstarkt lösenord' 
                            : passwordStrength === 'good' 
                              ? 'Bra lösenord' 
                              : password 
                                ? 'Lösenordet måste innehålla: minst 8 tecken, stor bokstav, liten bokstav, siffra och specialtecken'
                                : ''
                    }
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                {password && (
                  <Box mb={2}>
                    <TextField
                      type={showConfirmPassword ? 'text' : 'password'}
                      label="Bekräfta lösenord"
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      error={!!error && error.includes('matchar inte')}
                      helperText={error && error.includes('matchar inte') ? error : ''}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                )}
              </Box>

              {/* Submit Button */}
              <Box display="flex" justifyContent="flex-end" mt={4}>
                <Button type="submit" variant="contained" color="primary" size="large">
                  Spara ändringar
                </Button>
              </Box>

            </form>
            {error && !error.includes('matchar inte') && (
              <Box mt={2}>
                <Typography color="error">{error}</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </LogTheme>
  );
}

export default ChangeUser;
